import React, { memo, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useTheme } from '../../../contexts/ThemeContext';

// Skeleton loading component
const ChartSkeleton = () => (
  <div className="h-64 animate-pulse">
    <div className="flex items-end justify-between h-full space-x-2 px-4 pb-4">
      {[...Array(7)].map((_, i) => (
        <div
          key={i}
          className="bg-gray-200 dark:bg-gray-700 rounded-t"
          style={{ height: `${Math.random() * 60 + 20}%`, width: '12%' }}
        />
      ))}
    </div>
    <div className="flex justify-center mt-4 space-x-4">
      <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
      <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
    </div>
  </div>
);

// Custom tooltip component
const CustomTooltip = memo(({ active, payload, label, isDarkMode }) => {
  if (active && payload && payload.length) {
    return (
      <div 
        className={`p-3 rounded-lg border shadow-lg ${
          isDarkMode 
            ? 'bg-gray-800 border-gray-600 text-white' 
            : 'bg-white border-gray-200 text-gray-900'
        }`}
        role="tooltip"
        aria-label={`Dados para ${label}`}
      >
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
});

const MessageChart = memo(({ data, loading }) => {
  const { isDarkMode } = useTheme();

  // Memoizar dados processados para evitar recálculos desnecessários
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return [...data].reverse().map(item => ({
      date: item.formatted_date,
      enviadas: item.messages_sent || 0,
      respondidas: item.messages_responded || 0
    }));
  }, [data]);

  // Memoizar configurações do gráfico
  const chartConfig = useMemo(() => ({
    margin: { top: 5, right: 30, left: 20, bottom: 5 },
    lines: [
      {
        dataKey: "enviadas",
        stroke: "#3b82f6",
        name: "Mensagens Enviadas"
      },
      {
        dataKey: "respondidas", 
        stroke: "#10b981",
        name: "Mensagens Respondidas"
      }
    ]
  }), []);

  if (loading) {
    return <ChartSkeleton />;
  }

  if (!data || data.length === 0) {
    return (
      <div 
        className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400"
        role="img"
        aria-label="Nenhum dado disponível para exibir"
      >
        <svg className="w-12 h-12 mb-2 opacity-50" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        <p>Nenhum dado disponível</p>
      </div>
    );
  }

  return (
    <div className="h-64" role="img" aria-label="Gráfico de mensagens enviadas e respondidas por dia">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={chartConfig.margin}
          aria-label="Gráfico de linha mostrando mensagens enviadas e respondidas"
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            className="opacity-30"
            stroke={isDarkMode ? '#374151' : '#e5e7eb'}
          />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}
            stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}
            stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
            label={{ value: 'Quantidade', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip
            content={<CustomTooltip isDarkMode={isDarkMode} />}
          />
          <Legend />
          {chartConfig.lines.map((line) => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              stroke={line.stroke}
              strokeWidth={2}
              dot={{ fill: line.stroke, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: line.stroke, strokeWidth: 2 }}
              name={line.name}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

export default MessageChart;